import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { openLogin } from '../utils/openLogin';

function Settings() {
  const [apiKey, setApiKey] = useState('');
  const [loginStep, setLoginStep] = useState(1);
  const [loginData, setLoginData] = useState({ username: '', password: '', otp: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [hasSavedPassword, setHasSavedPassword] = useState(false);
  const [isApiConfigured, setIsApiConfigured] = useState(true);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tradingConfig, setTradingConfig] = useState({ paper_balance: 100000, trade_start_time: '09:30', trade_end_time: '15:00', mcx_trade_start_time: '09:00', mcx_trade_end_time: '23:00' });
  const [fyersConfig, setFyersConfig] = useState({ app_id: '', app_secret: '', redirect_uri: 'https://trade.fyers.in/api-login/redirect-uri/index.html' });
  const [fyersConnected, setFyersConnected] = useState(false);
  const [fyersStep, setFyersStep] = useState(1);
  const [fyersData, setFyersData] = useState({ fy_id: '', otp: '', totp: '', pin: '', auth_code: '' });
  const [fyersAuto, setFyersAuto] = useState({ fy_id: '', pin: '', totp_secret: '', configured: false, msg: '' });
  const [fyTrade, setFyTrade] = useState({ app_id: '', app_secret: '', auth_code: '', configured: false, connected: false, msg: '' });
  const [brokers, setBrokers] = useState([]);
  const [expandedBroker, setExpandedBroker] = useState(null);
  const [brokerValues, setBrokerValues] = useState({});
  const [tradeSound, setTradeSound] = useState(() => localStorage.getItem('tradeSound') === 'on');
  // Per-user broker connection (multi-user trading: your OWN mStock account)
  const [myBroker, setMyBroker] = useState({ configured: false, connected: false, apiKey: '', username: '', password: '', otp: '', step: 0, msg: '' });
  const [userBrokers, setUserBrokers] = useState([]);
  const [openUserBroker, setOpenUserBroker] = useState(null);
  const [userBrokerVals, setUserBrokerVals] = useState({});
  const [userBrokerMsg, setUserBrokerMsg] = useState('');
  const [userFyAuto, setUserFyAuto] = useState({ fy_id: '', pin: '', totp_secret: '', configured: false, open: false, msg: '' });

  useEffect(() => {
    api.userBrokers().then(r => { if (r.status === 'success') setUserBrokers(r.brokers); }).catch(() => {});
    api.userFyersAutoLoginStatus().then(r => { if (r && r.status === 'success') setUserFyAuto(p => ({ ...p, configured: !!r.configured, fy_id: r.fy_id || p.fy_id })); }).catch(() => {});
  }, []);

  const saveUserBroker = async (brokerId) => {
    const vals = userBrokerVals[brokerId] || {};
    const r = await api.userBrokerSaveConfig(brokerId, vals).catch(() => ({ status: 'error', message: 'Save failed' }));
    setUserBrokerMsg(r.message || '');
    if (r.status === 'success') {
      const rr = await api.userBrokers().catch(() => null);
      if (rr && rr.status === 'success') setUserBrokers(rr.brokers);
    }
  };

  const [fyersAuthCode, setFyersAuthCode] = useState('');
  const [fyersConnecting, setFyersConnecting] = useState(false);

  const startUserFyersLogin = async () => {
    const r = await openLogin(() => api.userFyersLoginUrl());
    if (r.status === 'success' && r.auth_url) {
      setFyersConnecting(true);
      setUserBrokerMsg('Fyers login opened in a new tab — authorize, then paste the auth_code from the redirect URL below');
    } else {
      setUserBrokerMsg(r.message || 'Could not build login URL — save your App ID first');
    }
  };

  const finishUserFyersConnect = async () => {
    const r = await api.userFyersConnect(fyersAuthCode).catch(() => ({ status: 'error', message: 'failed' }));
    setUserBrokerMsg(r.message || '');
    if (r.status === 'success') {
      setFyersConnecting(false);
      setFyersAuthCode('');
      const rr = await api.userBrokers().catch(() => null);
      if (rr && rr.status === 'success') setUserBrokers(rr.brokers);
    }
  };

  const testUserBroker = async (brokerId) => {
    setUserBrokerMsg(`Testing ${brokerId}...`);
    const r = await api.userBrokerCheck(brokerId).catch(() => ({ status: 'error', message: 'Test failed' }));
    setUserBrokerMsg(`${brokerId}: ${r.message || r.status}`);
  };

  // test chime (same synth the dashboard uses) — enabling from here also unlocks
  // the browser's audio permission via the click gesture
  const testDing = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[660, 0], [990, 0.18]].forEach(([f, when]) => {
        [[f, 0.12], [f * 2.01, 0.05]].forEach(([fr, gn]) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = 'sine'; o.frequency.value = fr;
          const t0 = ctx.currentTime + when;
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.exponentialRampToValueAtTime(gn, t0 + 0.015);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
          o.connect(g); g.connect(ctx.destination);
          o.start(t0); o.stop(t0 + 0.6);
        });
      });
    } catch (e) {}
  };

  useEffect(() => {
    loadSettings(); loadBrokers();
    api.userBrokerStatus().then(r => {
      if (r.status === 'success') setMyBroker(prev => ({ ...prev, configured: r.mstock_configured, connected: r.mstock_connected }));
    }).catch(() => {});
  }, []);

  const loadBrokers = async () => {
    try {
      const res = await api.listBrokers();
      if (res.status === 'success') setBrokers(res.brokers);
    } catch (e) {}
  };

  const [tradingModeInfo, setTradingModeInfo] = useState({ mode: 'paper', paper_balance: 0, live_balance: 0 });
  useEffect(() => {
    const loadMode = () => api.getTradingMode().then(setTradingModeInfo).catch(() => {});
    loadMode();
    const iv = setInterval(loadMode, 5000);
    return () => clearInterval(iv);
  }, []);

  const loadSettings = async () => {
    try {
      const configRes = await api.getConfig();
      if (configRes) {
        setTradingConfig({ paper_balance: configRes.paper_balance ?? 100000, trade_start_time: configRes.trade_start_time || '09:30', trade_end_time: configRes.trade_end_time || '15:00', mcx_trade_start_time: configRes.mcx_trade_start_time || '09:00', mcx_trade_end_time: configRes.mcx_trade_end_time || '23:00' });
        setIsLoggedIn(configRes.is_logged_in || false);
        if (configRes.api_key) setApiKey(configRes.api_key);
        if (configRes.username) setLoginData(prev => ({ ...prev, username: configRes.username }));
        setHasSavedPassword(configRes.has_saved_password || false);
        setIsApiConfigured(configRes.is_api_configured || false);
        if (configRes.is_logged_in) setLoginStep(3);
        else if (configRes.is_api_configured) setLoginStep(2);
        else setLoginStep(1);
      }
      try {
        const fyersRes = await api.fyersStatus();
        if (fyersRes) {
          setFyersConnected(fyersRes.connected || false);
          if (fyersRes.app_id) setFyersConfig(prev => ({ ...prev, app_id: fyersRes.app_id, app_secret: fyersRes.app_secret || prev.app_secret, redirect_uri: fyersRes.redirect_uri || prev.redirect_uri }));
          if (fyersRes.connected) setFyersStep(99);
          else if (fyersRes.configured) setFyersStep(2);
        }
      } catch (e) {}
      try {
        const a = await api.fyersAutoLoginStatus();
        if (a && a.status === 'success') setFyersAuto(prev => ({ ...prev, configured: !!a.configured, fy_id: a.fy_id || prev.fy_id }));
      } catch (e) {}
      try {
        const t = await api.fyersTradeStatus();
        if (t && t.status === 'success') setFyTrade(prev => ({ ...prev, configured: !!t.configured, connected: !!t.connected, app_id: t.app_id || prev.app_id }));
      } catch (e) {}
      setLoading(false);
    } catch (err) { setLoading(false); }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // ─── mStock Handlers ───

  const handleSaveApiKey = async () => {
    if (!apiKey) { showMessage('error', 'Enter API Key'); return; }
    try {
      const result = await api.saveApiKey(apiKey);
      if (result.status === 'success') { showMessage('success', 'Setup complete — now just login with User ID & Password'); setLoginStep(2); setIsApiConfigured(true); }
      else showMessage('error', result.message || 'Failed');
    } catch (err) { showMessage('error', 'Failed to save API Key'); }
  };

  const handleLogin = async () => {
    if (!loginData.username || !loginData.password) { showMessage('error', 'Enter UID and Password'); return; }
    try {
      const result = await api.login(loginData.username, loginData.password, rememberMe);
      if (result.status === 'success') {
        showMessage('success', rememberMe ? 'OTP sent! (password remembered for quick login)' : 'OTP sent!');
        if (rememberMe) setHasSavedPassword(true);
        setLoginStep(3);
      }
      else showMessage('error', result.message || 'Login failed');
    } catch (err) { showMessage('error', 'Login failed'); }
  };

  const handleQuickLogin = async () => {
    try {
      const result = await api.quickLogin();
      if (result.status === 'success') { showMessage('success', 'OTP sent — enter it below'); setLoginStep(3); }
      else showMessage('error', result.message || 'Quick login failed');
    } catch (err) { showMessage('error', 'Quick login failed'); }
  };

  const handleVerifyOTP = async () => {
    if (!loginData.otp) { showMessage('error', 'Enter OTP'); return; }
    try {
      const result = await api.verifyOTP(loginData.otp);
      if (result.status === 'success') { showMessage('success', 'mStock connected!'); setIsLoggedIn(true); setLoginStep(4); }
      else showMessage('error', result.message || 'Invalid OTP');
    } catch (err) { showMessage('error', 'OTP failed'); }
  };

  const handleVerifyTOTP = async () => {
    if (!loginData.otp) { showMessage('error', 'Enter TOTP'); return; }
    try {
      const result = await api.verifyTOTP(loginData.otp);
      if (result.status === 'success') { showMessage('success', 'mStock connected!'); setIsLoggedIn(true); setLoginStep(4); }
      else showMessage('error', result.message || 'Invalid TOTP');
    } catch (err) { showMessage('error', 'TOTP failed'); }
  };

  // ─── Fyers Handlers (Full Login Flow) ───

  const handleSaveFyersConfig = async () => {
    if (!fyersConfig.app_id || !fyersConfig.app_secret) { showMessage('error', 'Enter App ID and Secret'); return; }
    try {
      console.log('[FYERS] Saving config...', fyersConfig.app_id);
      const res = await api.fyersSaveConfig(fyersConfig.app_id, fyersConfig.app_secret, fyersConfig.redirect_uri);
      console.log('[FYERS] Config response:', res);
      if (res.status === 'success') { showMessage('success', 'Fyers config saved!'); setFyersStep(2); }
      else showMessage('error', res.message || 'Failed to save');
    } catch (err) {
      console.error('[FYERS] Config error:', err);
      showMessage('error', 'Failed to save Fyers config: ' + (err.message || err));
    }
  };

  const handleFyersLogin = async () => {
    try {
      const res = await openLogin(() => api.fyersLogin());
      if (res.status === 'success' && res.auth_url) {
        showMessage('success', 'Login window opened. Paste the auth_code from the redirect URL.');
        setFyersStep(6);
      } else showMessage('error', res.message || 'Failed');
    } catch (err) { showMessage('error', 'Fyers login failed'); }
  };

  const handleFyersSendOtp = async () => {
    if (!fyersData.fy_id) { showMessage('error', 'Enter Fyers User ID'); return; }
    try {
      const res = await api.fyersSendOtp(fyersData.fy_id);
      if (res.status === 'success') { showMessage('success', 'OTP sent to your mobile!'); setFyersStep(3); }
      else showMessage('error', res.message || 'Failed to send OTP');
    } catch (err) { showMessage('error', 'Failed to send OTP'); }
  };

  const handleFyersVerifyOtp = async () => {
    if (!fyersData.otp) { showMessage('error', 'Enter OTP'); return; }
    try {
      const res = await api.fyersVerifyOtp(fyersData.otp);
      if (res.status === 'success') { showMessage('success', 'OTP verified! Enter your PIN.'); setFyersStep(4); }
      else showMessage('error', res.message || 'Invalid OTP');
    } catch (err) { showMessage('error', 'OTP verification failed'); }
  };

  const handleFyersVerifyTotp = async () => {
    if (!fyersData.totp) { showMessage('error', 'Enter TOTP from Google Authenticator'); return; }
    try {
      const res = await api.fyersVerifyTotp(fyersData.totp);
      if (res.status === 'success') { showMessage('success', 'TOTP verified! Enter your PIN.'); setFyersStep(4); }
      else showMessage('error', res.message || 'Invalid TOTP');
    } catch (err) { showMessage('error', 'TOTP verification failed'); }
  };

  const handleFyersVerifyPin = async () => {
    if (!fyersData.pin) { showMessage('error', 'Enter your 4-digit PIN'); return; }
    try {
      const res = await api.fyersVerifyPin(fyersData.pin);
      if (res.status === 'success') { showMessage('success', 'PIN verified! Getting auth code...'); setFyersStep(5); handleFyersGetAuthCode(); }
      else showMessage('error', res.message || 'Invalid PIN');
    } catch (err) { showMessage('error', 'PIN verification failed'); }
  };

  const handleFyersGetAuthCode = async () => {
    try {
      const res = await api.fyersGetAuthCode();
      if (res.status === 'success' && res.auth_code) {
        showMessage('success', 'Auth code received! Generating token...');
        handleFyersGenerateToken(res.auth_code);
      } else {
        showMessage('error', res.message || 'Failed to get auth code. You may need to paste it manually.');
        setFyersStep(6);
      }
    } catch (err) { showMessage('error', 'Failed to get auth code'); setFyersStep(6); }
  };

  const handleFyersGenerateToken = async (code) => {
    const authCode = code || fyersData.auth_code;
    if (!authCode) { showMessage('error', 'Enter auth_code'); return; }
    try {
      const res = await api.fyersGenerateToken(authCode);
      if (res.status === 'success') { showMessage('success', 'Fyers connected! MCX trading enabled.'); setFyersConnected(true); setFyersStep(99); }
      else showMessage('error', res.message || 'Token generation failed');
    } catch (err) { showMessage('error', 'Token generation failed'); }
  };

  const handleTradingSave = async () => {
    // Show the REAL reason on failure (a bare "Failed to save" hid a 403 "Admin access
    // required" for clients — user 2026-09-25), and don't claim success on an error reply.
    const bal = Number(tradingConfig.paper_balance);
    if (!Number.isFinite(bal) || bal <= 0) { showMessage('error', 'Enter a paper balance greater than 0'); return; }
    try {
      const r = await api.updateConfig({ ...tradingConfig, paper_balance: bal });
      if (r && r.status === 'error') showMessage('error', r.message || 'Failed to save');
      else showMessage('success', 'Settings saved!');
    } catch (err) {
      const m = err && err.response && err.response.data && err.response.data.message;
      showMessage('error', m ? `Failed to save: ${m}` : 'Failed to save — check your connection and try again');
    }
  };

  // Per-user Mode + Option Selling (shown to every user — user 2026-09-24)
  const [mySelling, setMySelling] = useState(false);
  useEffect(() => {
    api.getMyTradingPrefs().then(r => { if (r && r.status === 'success') setMySelling(!!r.allow_option_selling); }).catch(() => {});
  }, []);
  // two taps instead of window.confirm (blocked on iPhone / Home-Screen app - the button did nothing)
  const liveArm = React.useRef(0);
  const setMyMode = async (m) => {
    if (m === 'live' && tradingModeInfo.mode !== 'live' && Date.now() - liveArm.current > 6000) {
      liveArm.current = Date.now();
      showMessage('error', 'LIVE = real orders on your own broker account. Tap Live again within 6 seconds to confirm.');
      return;
    }
    liveArm.current = 0;
    try {
      const r = await api.setMyTradingPrefs({ trade_mode: m });
      if (r.status === 'success') { setTradingModeInfo(prev => ({ ...prev, mode: m })); showMessage('success', `Trading mode: ${m.toUpperCase()}`); }
      else showMessage('error', r.message || 'Failed to save mode');
    } catch (e) { showMessage('error', 'Failed to save mode'); }
  };
  const toggleMySelling = async () => {
    const next = !mySelling;
    if (next && !window.confirm('Allow OPTION SELLING? It needs margin (~Rs.1 lakh+ per index lot) and can lose more than the premium collected.')) return;
    setMySelling(next);
    try {
      const r = await api.setMyTradingPrefs({ allow_option_selling: next });
      if (r.status !== 'success') { setMySelling(!next); showMessage('error', r.message || 'Failed to save'); }
      else showMessage('success', next ? 'Option selling ON' : 'Option selling OFF');
    } catch (e) { setMySelling(!next); showMessage('error', 'Failed to save'); }
  };

  // ─── Broker Hub Handlers ───

  const handleExpandBroker = (b) => {
    if (expandedBroker === b.id) { setExpandedBroker(null); return; }
    const init = {};
    b.fields.forEach(f => { init[f.key] = f.secret ? '' : (f.value || ''); });
    setBrokerValues(init);
    setExpandedBroker(b.id);
  };

  const handleSaveBrokerConfig = async (brokerId) => {
    try {
      const res = await api.saveBrokerConfig(brokerId, brokerValues);
      if (res.status === 'success') { showMessage('success', res.message); loadBrokers(); }
      else showMessage('error', res.message || 'Failed to save');
    } catch (err) { showMessage('error', 'Failed to save broker config'); }
  };

  const handleSelectBroker = async (brokerId) => {
    try {
      const res = await api.setLiveBroker(brokerId);
      if (res.broker) {
        showMessage(res.warning ? 'error' : 'success', res.warning || `Active broker: ${res.broker}`);
        loadBrokers();
      } else showMessage('error', res.error || 'Failed');
    } catch (err) { showMessage('error', 'Failed to select broker'); }
  };

  const handleKillSwitch = async () => {
    if (!window.confirm('KILL SWITCH: stop the engine and close ALL open positions immediately?')) return;
    try {
      const res = await api.killSwitch();
      showMessage('success', res.message);
    } catch (err) { showMessage('error', 'Kill switch failed'); }
  };

  const handleExportTrades = async () => {
    try {
      const token = localStorage.getItem('app_token') || '';
      const blob = await api.exportTrades(token);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `trades_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { showMessage('error', 'Export failed'); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  // Role isolation: master broker config, engine settings and the kill switch are
  // the OWNER's controls. Normal users only see THEIR own broker connection.
  const isAdmin = (() => {
    try { return (JSON.parse(localStorage.getItem('app_user') || '{}').role) === 'admin'; }
    catch { return false; }
  })();

  return (
    <div className="settings-page">
      <h2 style={{ color: '#00d9ff', marginBottom: '2rem' }}>Settings</h2>
      {message && <div className={`message ${message.type}`} style={{ position: 'fixed', left: 12, right: 12, bottom: 16, zIndex: 9998, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>{message.text}</div>}

      {!isAdmin && (
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Connect your own broker account below — the AI mirrors its trades into your account,
          sized to your balance. Platform-wide settings are managed by the admin.
        </p>
      )}

      {isAdmin && (<>
      <div style={{ margin: '1.4rem 0 0.6rem', padding: '0.6rem 0.9rem', borderRadius: 8, borderLeft: '5px solid #00d9ff', background: '#141a2a' }}>
        <div style={{ color: '#00d9ff', fontWeight: 800, fontSize: '1.05rem' }}>① MARKET DATA — prices only, NO orders <span className={`signal-badge ${fyersConnected ? 'signal-buy' : 'signal-sell'}`} style={{ marginLeft: 8 }}>{fyersConnected ? 'CONNECTED' : 'NOT LOGGED IN'}</span></div>
        <div style={{ color: '#aaa', fontSize: '0.8rem', marginTop: 2 }}>The bot reads prices for every user through this Fyers login. Nothing is bought or sold here. Log in every morning (Generate Token).</div>
      </div>
      {/* ─── Fyers Section (MCX) ─── */}
      <div className="card full-width">
        <div className="card-header">
          <span className="card-title">Market-data Fyers app (App ID / Secret)</span>
          {fyersConnected && <span className="signal-badge signal-buy">Connected</span>}
        </div>
        <div className="config-grid">
          <div className="config-item">
            <label>App ID</label>
            <input type="text" placeholder="e.g. ABCD1234-100" value={fyersConfig.app_id} onChange={e => setFyersConfig({...fyersConfig, app_id: e.target.value})} />
          </div>
          <div className="config-item">
            <label>App Secret</label>
            <input type="password" placeholder="App Secret" value={fyersConfig.app_secret} onChange={e => setFyersConfig({...fyersConfig, app_secret: e.target.value})} />
          </div>
        </div>
        <div className="config-item" style={{ marginTop: '0.5rem' }}>
          <label>Redirect URI</label>
          <input type="text" value={fyersConfig.redirect_uri} onChange={e => setFyersConfig({...fyersConfig, redirect_uri: e.target.value})} />
        </div>
        <button className="btn btn-start" onClick={handleSaveFyersConfig} style={{ marginTop: '1rem' }}>Save Config</button>
      </div>

      {!fyersConnected && fyersStep >= 2 && (
        <div className="card full-width">
          <div className="card-header"><span className="card-title">Daily login — MARKET DATA (every morning)</span></div>
          <p style={{ color: '#888', marginBottom: '1rem', fontSize: '0.85rem' }}>
            1. Click "Generate Token" to open Fyers login page<br/>
            2. Login and authorize — you'll be redirected to a URL<br/>
            3. Copy the <code style={{ color: '#00d9ff' }}>auth_code</code> parameter from the redirect URL and paste below
          </p>
          <button className="btn btn-start" onClick={handleFyersLogin} style={{ marginBottom: '1rem' }}>
            Generate Token
          </button>
          <div className="config-item">
            <label>Auth Code (from redirect URL)</label>
            <input type="text" placeholder="Paste auth_code here" value={fyersData.auth_code} onChange={e => setFyersData({...fyersData, auth_code: e.target.value})} />
          </div>
          <button className="btn btn-start" onClick={() => handleFyersGenerateToken()} style={{ marginTop: '1rem' }}>
            Connect
          </button>
        </div>
      )}

      {fyersConnected && fyersStep === 99 && (
        <div className="card full-width">
          <div className="card-header">
            <span className="card-title">Market data connected</span>
            <span className="signal-badge signal-buy">Active</span>
          </div>
          <p style={{ color: '#00ff88', marginBottom: '0.5rem' }}>Prices flowing for every user (NSE, BSE, MCX). No orders go through this login.</p>
          <button className="btn btn-stop" onClick={async () => { await api.logout(); setFyersConnected(false); setFyersStep(2); }}>Logout</button>
        </div>
      )}

      {/* ─── Fyers DAILY AUTO-LOGIN (no manual login each day) ─── */}
      <div className="card full-width" style={{ border: '1px solid #2a4a2a' }}>
        <div className="card-header">
          <span className="card-title">Market data auto-login (optional)</span>
          <span className={`signal-badge ${fyersAuto.configured ? 'signal-buy' : 'signal-hold'}`}>
            {fyersAuto.configured ? 'ENABLED — refreshes daily' : 'Not set up'}
          </span>
        </div>
        <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
          Fyers tokens expire every day (regulatory — no token lasts forever). Enter your Fyers ID,
          PIN and <b>TOTP secret</b> (the base32 seed from Fyers 2FA setup, not the 6-digit code) and the
          server logs in <b>automatically each morning</b> — MCX + data stay live without you touching it.
          <br /><span style={{ color: '#ffb020' }}>Security: these are stored on the server so it can log in for you.
          A server breach would expose Fyers account access. Only enable if you accept that.</span>
        </p>
        <div className="config-grid">
          <div className="config-item">
            <label>Fyers ID (fy_id)</label>
            <input type="text" placeholder="e.g. XA12345" value={fyersAuto.fy_id}
              onChange={e => setFyersAuto({ ...fyersAuto, fy_id: e.target.value })} />
          </div>
          <div className="config-item">
            <label>PIN</label>
            <input type="password" placeholder={fyersAuto.configured ? '•••• (leave blank to keep)' : '4-digit PIN'}
              value={fyersAuto.pin} onChange={e => setFyersAuto({ ...fyersAuto, pin: e.target.value })} />
          </div>
        </div>
        <div className="config-item" style={{ marginTop: '0.5rem' }}>
          <label>TOTP Secret (base32 seed)</label>
          <input type="password" placeholder={fyersAuto.configured ? '•••• (leave blank to keep)' : 'e.g. JBSWY3DPEHPK3PXP'}
            value={fyersAuto.totp_secret} onChange={e => setFyersAuto({ ...fyersAuto, totp_secret: e.target.value })} />
        </div>
        <button className="btn btn-start" style={{ marginTop: '0.8rem' }} onClick={async () => {
          setFyersAuto(a => ({ ...a, msg: 'Saving & testing login…' }));
          const r = await api.fyersAutoLoginConfig(fyersAuto.fy_id, fyersAuto.pin, fyersAuto.totp_secret)
            .catch(() => ({ status: 'error', message: 'failed' }));
          setFyersAuto(a => ({ ...a, configured: r.status === 'success' || a.configured, pin: '', totp_secret: '', msg: r.message || '' }));
          if (r.status === 'success') setFyersConnected(true);
        }}>Save &amp; Enable Auto-Login</button>
        {fyersAuto.msg && <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: fyersAuto.configured ? '#00ff88' : '#ffb020' }}>{fyersAuto.msg}</p>}
      </div>

      <div style={{ margin: '1.4rem 0 0.6rem', padding: '0.6rem 0.9rem', borderRadius: 8, borderLeft: '5px solid #ff9f1a', background: '#141a2a' }}>
        <div style={{ color: '#ff9f1a', fontWeight: 800, fontSize: '1.05rem' }}>② LIVE TRADING — admin's real orders <span className={`signal-badge ${fyTrade.connected ? 'signal-buy' : 'signal-sell'}`} style={{ marginLeft: 8 }}>{fyTrade.connected ? 'TRADING API CONNECTED' : 'TRADING API NOT CONNECTED'}</span> <span className={`signal-badge ${tradingModeInfo.mode === 'live' ? 'signal-sell' : 'signal-hold'}`} style={{ marginLeft: 6 }}>Admin is {String(tradingModeInfo.mode || 'paper').toUpperCase()}</span></div>
        <div style={{ color: '#aaa', fontSize: '0.8rem', marginTop: 2 }}>Admin's real buy/sell orders go ONLY through this Trading API. Then set Trading Mode = Live (Trading Settings below). Users trade in their own accounts.</div>
      </div>
      {/* ─── SEPARATE Fyers TRADING API (orders only; distinct from the data feed) ─── */}
      <div className="card full-width" style={{ border: '1px solid #4a3a2a' }}>
        <div className="card-header">
          <span className="card-title">Admin LIVE trading — Fyers Trading API</span>
          <span className={`signal-badge ${fyTrade.connected ? 'signal-buy' : (fyTrade.configured ? 'signal-hold' : 'signal-sell')}`}>
            {fyTrade.connected ? 'CONNECTED — admin live orders go here' : (fyTrade.configured ? 'Saved — log in below (every morning)' : 'Not set — admin cannot trade live')}
          </span>
        </div>
        <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
          This is a <b>separate</b> Fyers app used <b>only to place orders</b>, kept distinct from the
          auto-renewing <b>data</b> feed above (data = scanning/prices; trading = order execution).
          You connect this one <b>yourself</b> and its token is <b>not auto-renewed</b> — you re-connect it when it expires.
          <br /><span style={{ color: '#ffb020' }}>Log in here with the Fyers account admin should trade. It must NOT be the same Fyers account a live user (vijay) trades - that would double every order.</span>
        </p>
        <div className="config-grid">
          <div className="config-item">
            <label>Trading App ID</label>
            <input type="text" placeholder="e.g. WXYZ9876-100" value={fyTrade.app_id}
              onChange={e => setFyTrade({ ...fyTrade, app_id: e.target.value })} />
          </div>
          <div className="config-item">
            <label>Trading App Secret</label>
            <input type="password" placeholder={fyTrade.configured ? '•••• (leave blank to keep)' : 'App Secret'}
              value={fyTrade.app_secret} onChange={e => setFyTrade({ ...fyTrade, app_secret: e.target.value })} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.8rem', flexWrap: 'wrap' }}>
          <button className="btn btn-start" onClick={async () => {
            const r = await api.fyersTradeConfig(fyTrade.app_id, fyTrade.app_secret).catch(() => ({ status: 'error', message: 'failed' }));
            setFyTrade(p => ({ ...p, configured: r.status === 'success' || p.configured, app_secret: '', msg: r.message || '' }));
          }}>Save Trading API</button>
          <button className="btn" style={{ border: '1px solid #00d9ff', color: '#00d9ff', background: 'transparent' }} onClick={async () => {
            const r = await openLogin(() => api.fyersTradeLoginUrl());
            if (r.status === 'success' && r.auth_url) { setFyTrade(p => ({ ...p, msg: 'Login opened — authorize, then paste the auth_code below' })); }
            else setFyTrade(p => ({ ...p, msg: r.message || 'Save Trading App ID/Secret first' }));
          }}>Login (open Fyers)</button>
        </div>
        <div className="config-item" style={{ marginTop: '0.6rem' }}>
          <label>Auth Code (from redirect URL)</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input type="text" placeholder="Paste auth_code here" value={fyTrade.auth_code}
              onChange={e => setFyTrade({ ...fyTrade, auth_code: e.target.value })} style={{ flex: 1 }} />
            <button className="btn btn-start" onClick={async () => {
              const r = await api.fyersTradeConnect(fyTrade.auth_code).catch(() => ({ status: 'error', message: 'failed' }));
              setFyTrade(p => ({ ...p, connected: r.status === 'success' || p.connected, auth_code: '', msg: r.message || '' }));
            }}>Connect</button>
          </div>
        </div>
        {fyTrade.msg && <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: fyTrade.connected ? '#00ff88' : '#ffb020' }}>{fyTrade.msg}</p>}
      </div>

      <details className="card full-width" style={{ marginTop: '1.4rem' }}>
        <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.85rem' }}>③ Other brokers — not used for admin (mStock / Broker Hub) · tap to open</summary>
      {/* ─── Broker Hub ─── */}
      <div className="card full-width">
        <div className="card-header">
          <span className="card-title">Broker Hub — Select &amp; Configure</span>
        </div>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Choose your broker and save its API credentials. mStock and Fyers are fully integrated for live orders;
          other brokers store credentials now and light up as integrations land.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
          {brokers.map(b => (
            <div key={b.id} style={{ border: b.active ? '1px solid #00ff88' : '1px solid #333', borderRadius: '8px', padding: '0.9rem', background: '#111' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <strong style={{ color: '#eee', fontSize: '0.95rem' }}>{b.name}</strong>
                {b.active && <span className="signal-badge signal-buy">Active</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {b.exchanges.map(ex => (
                  <span key={ex} style={{ fontSize: '0.7rem', color: ex === 'MCX' ? '#ffb020' : '#00d9ff', border: '1px solid #333', borderRadius: '4px', padding: '0 6px' }}>{ex}</span>
                ))}
                <span style={{ fontSize: '0.7rem', color: b.integration === 'full' ? '#00ff88' : '#888', border: '1px solid #333', borderRadius: '4px', padding: '0 6px' }}>
                  {b.integration === 'full' ? 'Fully integrated' : 'Config-ready'}
                </span>
                {b.connected && <span style={{ fontSize: '0.7rem', color: '#00ff88', border: '1px solid #00ff88', borderRadius: '4px', padding: '0 6px' }}>Connected</span>}
                {!b.connected && b.configured && <span style={{ fontSize: '0.7rem', color: '#ffb020', border: '1px solid #ffb020', borderRadius: '4px', padding: '0 6px' }}>Configured</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn" style={{ fontSize: '0.75rem', padding: '4px 10px', background: '#222', color: '#00d9ff', border: '1px solid #00d9ff' }} onClick={() => handleExpandBroker(b)}>
                  {expandedBroker === b.id ? 'Close' : 'Configure'}
                </button>
                {!b.active && (
                  <button className="btn" style={{ fontSize: '0.75rem', padding: '4px 10px', background: '#222', color: '#00ff88', border: '1px solid #00ff88' }} onClick={() => handleSelectBroker(b.id)}>
                    Set Active
                  </button>
                )}
              </div>
              {expandedBroker === b.id && (
                <div style={{ marginTop: '0.75rem', borderTop: '1px solid #333', paddingTop: '0.75rem' }}>
                  {b.note && <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem' }}>{b.note}</p>}
                  {b.fields.map(f => (
                    <div className="config-item" key={f.key} style={{ marginBottom: '0.5rem' }}>
                      <label style={{ fontSize: '0.75rem' }}>{f.label}{f.set ? ' (saved)' : ''}</label>
                      <input
                        type={f.secret ? 'password' : 'text'}
                        placeholder={f.set ? '••••••  (leave blank to keep)' : f.label}
                        value={brokerValues[f.key] || ''}
                        onChange={e => setBrokerValues({ ...brokerValues, [f.key]: e.target.value })}
                      />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="btn btn-start" style={{ fontSize: '0.8rem' }} onClick={() => handleSaveBrokerConfig(b.id)}>Save Credentials</button>
                    {b.docs && <a href={b.docs} target="_blank" rel="noreferrer" style={{ color: '#00d9ff', fontSize: '0.75rem' }}>API docs ↗</a>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ─── mStock Connect: UID/Mobile + Password + OTP — that's all ─── */}
      <div className="card full-width">
        <div className="card-header">
          <span className="card-title">mStock (NSE / NFO — Equity &amp; F&amp;O Options)</span>
          {isLoggedIn && <span className="signal-badge signal-buy">Connected</span>}
        </div>
        {!isApiConfigured && (
          <p style={{ color: '#ffb020', fontSize: '0.85rem' }}>
            One-time technical setup needed first — open "Advanced" below, paste your mStock API key once, and never think about it again.
          </p>
        )}
      </div>

      {!isLoggedIn && isApiConfigured && (
        <div className="card full-width">
          <div className="card-header"><span className="card-title">Connect mStock — User ID &amp; Password</span></div>
          {hasSavedPassword && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #00d9ff', borderRadius: '8px' }}>
              <p style={{ color: '#00d9ff', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Credentials saved — one click sends today's OTP, no typing needed.
              </p>
              <button className="btn btn-start" onClick={handleQuickLogin}>⚡ Quick Login — Send OTP</button>
            </div>
          )}
          <div className="config-grid">
            <div className="config-item">
              <label>User ID / Mobile</label>
              <input type="text" placeholder="mStock User ID or mobile number" value={loginData.username} onChange={e => setLoginData({...loginData, username: e.target.value})} />
            </div>
            <div className="config-item">
              <label>Password</label>
              <input type="password" placeholder="Password" value={loginData.password} onChange={e => setLoginData({...loginData, password: e.target.value})} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', color: '#aaa', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} style={{ width: 'auto' }} />
            Remember password for one-click daily login (stored on this computer)
          </label>
          <button className="btn btn-start" onClick={handleLogin} style={{ marginTop: '1rem' }}>Login & Send OTP</button>
        </div>
      )}

      {!isLoggedIn && loginStep >= 3 && loginData.username && (
        <div className="card full-width">
          <div className="card-header"><span className="card-title">Enter OTP / TOTP</span></div>
          <div className="config-item">
            <label>Code</label>
            <input type="text" placeholder="6-digit code" value={loginData.otp} onChange={e => setLoginData({...loginData, otp: e.target.value})} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button className="btn btn-start" onClick={handleVerifyOTP}>Verify SMS OTP</button>
            <button className="btn btn-start" onClick={handleVerifyTOTP} style={{ background: '#333', borderColor: '#00d9ff' }}>Verify TOTP</button>
          </div>
        </div>
      )}

      {isLoggedIn && (
        <div className="card full-width">
          <div className="card-header">
            <span className="card-title">mStock Connected</span>
            <span className="signal-badge signal-buy">Active</span>
          </div>
          <button className="btn btn-stop" onClick={async () => { await api.logout(); setIsLoggedIn(false); setLoginStep(2); }}>Logout</button>
        </div>
      )}

      {/* ─── Advanced: one-time API key (hidden plumbing, not part of daily use) ─── */}
      <details className="card full-width" open={!isApiConfigured}>
        <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.85rem' }}>
          Advanced — one-time technical setup (mStock API key){isApiConfigured ? ' ✓ configured' : ' — required once'}
        </summary>
        <p style={{ color: '#888', fontSize: '0.8rem', margin: '0.75rem 0 0.5rem' }}>
          The broker requires this key to allow any trading app to connect. Generate it once at tradingapi.mstock.com,
          save it here, and the app handles it automatically forever — your daily login is just User ID + Password + OTP.
        </p>
        <div className="config-item">
          <label>API Key</label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input type="password" placeholder="Enter API Key (one time)" value={apiKey} onChange={e => setApiKey(e.target.value)} style={{ flex: 1 }} />
            <button className="btn btn-start" onClick={handleSaveApiKey}>Save</button>
          </div>
        </div>
      </details>

      </details>
      {/* Alice Blue removed — Fyers covers NSE/BSE/MCX, mStock covers NSE/BSE trading. */}

      {/* ─── System Health (every blockage + its fix) ─── */}
      <SystemHealthCard />
      </>)}

      {/* ─── My Broker Account — per-user trading (multi-user, broker-neutral) ─── */}
      {!isAdmin && (<div className="card full-width">
        <div className="card-header">
          <span className="card-title">My Broker Account</span>
          {(() => {
            const connected = userBrokers.filter(b => b.connected).map(b => b.name.split(' ')[0]);
            return connected.length
              ? <span className="signal-badge signal-buy">CONNECTED: {connected.join(' + ')} — trades mirror into your account</span>
              : <span className="signal-badge signal-hold">{userBrokers.some(b => b.configured) ? 'Credentials saved — connect below' : 'Not connected'}</span>;
          })()}
        </div>

        {/* Full broker list — each user picks THEIR broker; no broker is assumed */}
        <div>
          <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
            Pick <strong>your</strong> broker and connect it — the AI mirrors every qualified trade
            into your account, sized to your balance. The platform works according to what your broker
            supports (exchanges shown per broker). mStock &amp; Fyers are live today;
            other brokers store your credentials and go live as their integrations land.
          </div>
          {userBrokerMsg && <div style={{ color: '#00d9ff', fontSize: '0.78rem', marginBottom: '0.5rem' }}>{userBrokerMsg}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.6rem' }}>
            {userBrokers.map(b => (
              <div key={b.id} style={{ border: b.connected ? '1px solid #00ff88' : b.configured ? '1px solid #00d9ff' : '1px solid #333', borderRadius: '8px', padding: '0.7rem', background: '#111' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ color: '#eee', fontSize: '0.85rem' }}>{b.name}</strong>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700, color: b.connected ? '#00ff88' : b.configured ? '#00d9ff' : '#666' }}>
                    {b.connected ? 'CONNECTED' : b.configured ? 'SAVED' : b.integration === 'full' ? 'READY' : 'CONFIG ONLY'}
                  </span>
                </div>
                <div style={{ color: '#777', fontSize: '0.68rem', margin: '0.25rem 0' }}>
                  {b.exchanges.join(' · ')}{b.integration !== 'full' && ' — integration pending'}
                </div>
                {openUserBroker === b.id ? (
                  <div>
                    {b.fields.map(f => (
                      <input key={f.key} type={f.secret ? 'password' : 'text'} placeholder={f.label}
                        defaultValue={f.value}
                        onChange={e => setUserBrokerVals(p => ({ ...p, [b.id]: { ...(p[b.id] || {}), [f.key]: e.target.value } }))}
                        style={{ width: '100%', marginTop: '0.3rem' }} />
                    ))}
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={() => saveUserBroker(b.id)}>Save</button>
                      <button className="btn" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={() => setOpenUserBroker(null)}>Close</button>
                    </div>
                    {/* mStock daily OTP login — lives INSIDE the mStock card */}
                    {b.id === 'mstock' && b.configured && (
                      <div style={{ marginTop: '0.5rem', borderTop: '1px solid #2a2a4a', paddingTop: '0.5rem' }}>
                        <div style={{ color: '#888', fontSize: '0.68rem', marginBottom: '0.3rem' }}>Daily login (OTP):</div>
                        <input placeholder="mStock username/mobile" value={myBroker.username}
                          onChange={e => setMyBroker(p => ({ ...p, username: e.target.value }))} style={{ width: '100%', marginBottom: '0.3rem' }} />
                        <input placeholder="Password" type="password" value={myBroker.password}
                          onChange={e => setMyBroker(p => ({ ...p, password: e.target.value }))} style={{ width: '100%', marginBottom: '0.3rem' }} />
                        <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={async () => {
                          const r = await api.userMstockLogin(myBroker.username, myBroker.password).catch(() => ({ status: 'error', message: 'failed' }));
                          setMyBroker(p => ({ ...p, step: r.status === 'success' ? 1 : 0, msg: r.message || '' }));
                        }}>Send OTP</button>
                        {myBroker.step === 1 && (
                          <div style={{ marginTop: '0.3rem' }}>
                            <input placeholder="OTP" value={myBroker.otp}
                              onChange={e => setMyBroker(p => ({ ...p, otp: e.target.value }))} style={{ width: '100px', marginRight: '0.3rem' }} />
                            <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={async () => {
                              const r = await api.userMstockVerifyOtp(myBroker.otp).catch(() => ({ status: 'error', message: 'failed' }));
                              setMyBroker(p => ({ ...p, connected: r.status === 'success', step: 0, msg: r.message || (r.status === 'success' ? 'Connected!' : '') }));
                              if (r.status === 'success') { const rr = await api.userBrokers().catch(() => null); if (rr && rr.status === 'success') setUserBrokers(rr.brokers); }
                            }}>Verify &amp; Connect</button>
                          </div>
                        )}
                        {myBroker.msg && <div style={{ color: '#00d9ff', fontSize: '0.68rem', marginTop: '0.3rem' }}>{myBroker.msg}</div>}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-paper" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={() => setOpenUserBroker(b.id)}>
                      {b.configured ? 'Edit credentials' : 'Add credentials'}
                    </button>
                    {/* CONNECT: appears once credentials are saved */}
                    {b.configured && b.id === 'fyers' && !b.connected && (
                      <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={startUserFyersLogin}>Connect</button>
                    )}
                    {b.configured && b.id === 'mstock' && !b.connected && (
                      <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }} onClick={() => setOpenUserBroker('mstock')}>Connect</button>
                    )}
                    {b.configured && (b.id === 'mstock' || b.id === 'fyers') && (
                      <button className="btn" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem', border: '1px solid #00d9ff', color: '#00d9ff', background: 'transparent' }} onClick={() => testUserBroker(b.id)}>Test</button>
                    )}
                    {/* DISCONNECT: appears only when connected — client can log out of their broker */}
                    {b.connected && (b.id === 'mstock' || b.id === 'fyers') && (
                      <button className="btn btn-stop" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }}
                        onClick={async () => {
                          setUserBrokerMsg(`Disconnecting ${b.name}…`);
                          const r = await api.userBrokerDisconnect(b.id).catch(() => ({ status: 'error', message: 'failed' }));
                          setUserBrokerMsg(r.message || (r.status === 'success' ? 'Disconnected' : 'Failed to disconnect'));
                          const rr = await api.userBrokers().catch(() => null);
                          if (rr && rr.status === 'success') setUserBrokers(rr.brokers);
                        }}>Disconnect</button>
                    )}
                  </div>
                )}
                {/* Fyers auth-code paste step (after the login tab) */}
                {b.id === 'fyers' && fyersConnecting && (
                  <div style={{ marginTop: '0.4rem' }}>
                    <input placeholder="Paste auth_code from redirect URL" value={fyersAuthCode}
                      onChange={e => setFyersAuthCode(e.target.value)} style={{ width: '100%' }} />
                    <button className="btn btn-start" style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem', marginTop: '0.3rem' }} onClick={finishUserFyersConnect}>
                      Complete Connect
                    </button>
                  </div>
                )}
                {/* Fyers DAILY AUTO-LOGIN (per user) — no manual login each day */}
                {b.id === 'fyers' && b.configured && (
                  <div style={{ marginTop: '0.5rem', borderTop: '1px solid #2a4a2a', paddingTop: '0.4rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#00ff88', fontSize: '0.68rem', fontWeight: 700 }}>
                        Auto-Login {userFyAuto.configured ? '✓ daily refresh ON' : '(daily token refresh)'}
                      </span>
                      <button className="btn" style={{ fontSize: '0.66rem', padding: '0.15rem 0.5rem', background: 'transparent', color: '#00d9ff', border: '1px solid #00d9ff' }}
                        onClick={() => setUserFyAuto(p => ({ ...p, open: !p.open }))}>{userFyAuto.open ? 'Hide' : 'Set up'}</button>
                    </div>
                    {userFyAuto.open && (
                      <div style={{ marginTop: '0.3rem' }}>
                        <div style={{ color: '#888', fontSize: '0.64rem', marginBottom: '0.3rem' }}>
                          Enter once — server logs into YOUR Fyers each morning (no daily manual login). TOTP secret = the base32 seed from Fyers 2FA (not the 6-digit code).
                          <span style={{ color: '#ffb020' }}> Stored on server so it can log in for you.</span>
                        </div>
                        <input placeholder="Fyers ID (fy_id)" value={userFyAuto.fy_id}
                          onChange={e => setUserFyAuto(p => ({ ...p, fy_id: e.target.value }))} style={{ width: '100%', marginBottom: '0.25rem' }} />
                        <input placeholder={userFyAuto.configured ? 'PIN (leave blank to keep)' : 'PIN'} type="password" value={userFyAuto.pin}
                          onChange={e => setUserFyAuto(p => ({ ...p, pin: e.target.value }))} style={{ width: '100%', marginBottom: '0.25rem' }} />
                        <input placeholder={userFyAuto.configured ? 'TOTP secret (leave blank to keep)' : 'TOTP secret (base32)'} type="password" value={userFyAuto.totp_secret}
                          onChange={e => setUserFyAuto(p => ({ ...p, totp_secret: e.target.value }))} style={{ width: '100%', marginBottom: '0.25rem' }} />
                        <button className="btn btn-start" style={{ fontSize: '0.7rem', padding: '0.3rem 0.7rem' }} onClick={async () => {
                          setUserFyAuto(p => ({ ...p, msg: 'Saving & testing…' }));
                          const r = await api.userFyersAutoLogin(userFyAuto.fy_id, userFyAuto.pin, userFyAuto.totp_secret).catch(() => ({ status: 'error', message: 'failed' }));
                          setUserFyAuto(p => ({ ...p, configured: r.status === 'success' || p.configured, pin: '', totp_secret: '', msg: r.message || '' }));
                          if (r.status === 'success') { const rr = await api.userBrokers().catch(() => null); if (rr && rr.status === 'success') setUserBrokers(rr.brokers); }
                        }}>Save &amp; Enable Auto-Login</button>
                        {userFyAuto.msg && <div style={{ fontSize: '0.66rem', marginTop: '0.25rem', color: userFyAuto.configured ? '#00ff88' : '#ffb020' }}>{userFyAuto.msg}</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>)}

      <>
      {/* ─── Trading Settings — shown to EVERY user (per-user balance/mode/selling) ─── */}
      <div className="card full-width">
        <div className="card-header"><span className="card-title">Trading Settings</span></div>
        <div className="config-grid">
          <div className="config-item">
            <label>Trading Mode</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
              <button className={`btn ${tradingModeInfo.mode === 'paper' ? 'btn-paper-active' : 'btn-paper'}`} onClick={() => setMyMode('paper')}>Paper</button>
              <button className={`btn ${tradingModeInfo.mode === 'live' ? 'btn-live-active' : 'btn-live'}`} onClick={() => setMyMode('live')}>Live</button>
              <button className={`btn ${tradingModeInfo.mode === 'off' ? 'btn-stop' : 'btn-paper'}`} onClick={() => setMyMode('off')}>Off</button>
            </div>
            <p style={{ color: '#666', fontSize: '0.72rem', margin: '0.3rem 0 0' }}>{isAdmin ? 'Live = real orders through the Trading API (section ② above). The market-data login never places orders.' : 'Live places real orders on YOUR connected broker.'}</p>
          </div>

          <div className="config-item">
            <label>Trade Sounds</label>
            <button className={`btn ${tradeSound ? 'btn-start' : 'btn-paper'}`} style={{ marginTop: '0.3rem' }} onClick={() => {
              const v = !tradeSound; setTradeSound(v);
              localStorage.setItem('tradeSound', v ? 'on' : 'off');
              if (v) testDing();
            }}>{tradeSound ? '🔔 ON — chime on open / profit / loss' : '🔕 OFF'}</button>
          </div>
          {/* LIVE mode: show ONLY the real broker balance (no paper field).
              PAPER mode: show the editable paper balance. */}
          {tradingModeInfo.mode === 'live' ? (
            <div className="config-item">
              <label style={{ color: '#888' }}>Live Balance — real broker account</label>
              <p style={{ color: '#00ff88', fontSize: '1.1rem', fontWeight: 700, margin: '0.3rem 0 0' }}>
                ₹{Number(tradingModeInfo.live_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </p>
              <p style={{ color: '#666', fontSize: '0.72rem', margin: '0.2rem 0 0' }}>Live from your broker · updates every 5s</p>
            </div>
          ) : (
            <div className="config-item">
              <label>Paper Balance (Rs.) — practice capital</label>
              <input type="number" value={tradingConfig.paper_balance} onChange={e => setTradingConfig({...tradingConfig, paper_balance: parseFloat(e.target.value)})} />
            </div>
          )}
          <div className="config-item">
            <label style={{ color: '#888' }}>Position sizing</label>
            <p style={{ color: '#00d9ff', fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
              Automatic — each trade uses your full available balance (live: broker balance, paper: above), sized down only by the per-trade risk limit. Grows and shrinks with the account.
            </p>
          </div>
        </div>
        <div className="card-header" style={{ marginTop: '1.25rem' }}><span className="card-title" style={{ fontSize: '0.9rem' }}>Risk Controls — Automatic</span></div>
        <div style={{ color: '#888', fontSize: '0.8rem', lineHeight: 1.7, marginBottom: '0.5rem' }}>
          <span style={{ color: '#00d9ff' }}>No manual limits.</span> The AI governor manages everything from your actual balance:
          <span style={{ color: '#aaa' }}> day-loss stop at 8% of capital · profit-giveback lock after a good day · cautious mode after 2 straight losses · 30-min block on losing symbols · unlimited trades, uncapped winners.</span>
        </div>
        <div className="config-grid" style={{ marginTop: '0.5rem' }}>
          <div className="config-item">
            <label style={{ color: '#888' }}>Stop Loss &amp; Target</label>
            <p style={{ color: '#00d9ff', fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
              AI-decided per trade — width scales with the pick's conviction and with real evidence
              from past exits (if stops on a stock keep recovering, they widen automatically).
              No manual setting.
            </p>
          </div>
        </div>
        <div className="config-grid" style={{ marginTop: '1rem' }}>
          <div className="config-item">
            <label>NSE / NFO Start Time</label>
            <input type="time" disabled={!isAdmin} value={tradingConfig.trade_start_time} onChange={e => setTradingConfig({...tradingConfig, trade_start_time: e.target.value})} />
          </div>
          <div className="config-item">
            <label>NSE / NFO End Time</label>
            <input type="time" disabled={!isAdmin} value={tradingConfig.trade_end_time} onChange={e => setTradingConfig({...tradingConfig, trade_end_time: e.target.value})} />
          </div>
        </div>
        <div className="config-grid" style={{ marginTop: '0.5rem' }}>
          <div className="config-item">
            <label>MCX Start Time</label>
            <input type="time" disabled={!isAdmin} value={tradingConfig.mcx_trade_start_time} onChange={e => setTradingConfig({...tradingConfig, mcx_trade_start_time: e.target.value})} />
          </div>
          <div className="config-item">
            <label>MCX End Time</label>
            <input type="time" disabled={!isAdmin} value={tradingConfig.mcx_trade_end_time} onChange={e => setTradingConfig({...tradingConfig, mcx_trade_end_time: e.target.value})} />
          </div>
        </div>
        <p style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.5rem' }}>
          New trades open only within these times. Existing positions exit anytime until market close.
          {!isAdmin && <span style={{ color: '#666' }}> Trading hours are platform-wide and set by the admin.</span>}
        </p>
        <button className="btn btn-start" onClick={handleTradingSave} style={{ marginTop: '1rem' }}>Save</button>
      </div>

      {/* ─── Emergency & Journal — ADMIN only (kill switch stops the engine for all) ─── */}
      {isAdmin && (
      <div className="card full-width" style={{ border: '1px solid #662222' }}>
        <div className="card-header"><span className="card-title" style={{ color: '#ff5555' }}>Emergency &amp; Trade Journal</span></div>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Kill Switch stops the engine and closes every open position at market immediately.
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn btn-stop" onClick={handleKillSwitch}>🛑 KILL SWITCH — Stop &amp; Close All</button>
          <button className="btn" style={{ background: '#222', color: '#00d9ff', border: '1px solid #00d9ff' }} onClick={handleExportTrades}>⬇ Export Trades (CSV)</button>
        </div>
      </div>
      )}
      </>
    </div>
  );
}

function SystemHealthCard() {
  const [health, setHealth] = React.useState(null);
  React.useEffect(() => {
    const load = () => api.assistantHealth().then(setHealth).catch(() => {});
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, []);
  if (!health) return null;
  const issues = health.items.filter(i => i.level !== 'ok');
  return (
    <div className="card full-width">
      <div className="card-header">
        <span className="card-title">System Health</span>
        <span style={{
          fontSize: '0.72rem', fontWeight: 600,
          color: health.can_trade ? '#00ff88' : '#ff4444',
        }}>{health.summary}</span>
      </div>
      {issues.length === 0 ? (
        <p style={{ color: '#00ff88', fontSize: '0.8rem' }}>✅ No blockages — everything operational.</p>
      ) : issues.map((it, i) => (
        <div key={i} style={{ padding: '0.35rem 0', borderBottom: '1px solid #1a1a2e', fontSize: '0.75rem' }}>
          <span style={{ color: it.level === 'block' ? '#ff4444' : '#f59e0b', fontWeight: 600 }}>
            {it.level === 'block' ? '⛔' : '⚠️'} {it.title}
          </span>
          <span style={{ color: '#999' }}> — {it.detail}</span>
          {it.solution && <span style={{ color: '#00d9ff' }}> FIX: {it.solution}</span>}
        </div>
      ))}
    </div>
  );
}

export default Settings;
